import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';
import {
  CampaignPlanAllocationReportConfig,
  CampaignPlanDetails,
  CampaignPlanError,
  CampaignPlanFilterOptions,
  TouchedPoint,
} from 'components/common/types/CampaignPlan.types';
import { ReportType, SelectedLine } from 'components/common/AllocationReport/AllocationReport.types';
import { publicDefaultLocaleCodes } from 'components/pages/PublicCampaignPlan/publicDefaultLocaleCodes';

export const getSharedCampaignPlan = createSelector(
  [(state: RootState) => state.sharedCampaignPlan],
  (sharedCampaignPlan) => sharedCampaignPlan,
);

export const getIsSharedCampaign = createSelector([getSharedCampaignPlan], (sharedCampaignPlan) =>
  Boolean(sharedCampaignPlan.campaignPlanDetails),
);

export const getSharedCampaignPlanMetaData = createSelector(
  [getSharedCampaignPlan],
  (sharedCampaignPlan) => sharedCampaignPlan.campaignPlanDetails?.metaData ?? { latitude: 0, longitude: 0 },
);

export const getSharedCampaignPlanAssets = createSelector(
  [getSharedCampaignPlan],
  (sharedCampaignPlan) => sharedCampaignPlan.campaignPlanDetails?.assets?.filteredAssets || [],
);

export const getNumberOfSharedCampaignPlanAssets = createSelector(
  [getSharedCampaignPlanAssets],
  (sharedCampaignPlanAssets) => sharedCampaignPlanAssets.length,
);

export const getIsLoading = createSelector(
  [getSharedCampaignPlan],
  (sharedCampaignPlan) => sharedCampaignPlan.isLoading,
);

export const getCampaignPlanDetails = createSelector(
  [getSharedCampaignPlan],
  (sharedCampaignPlan): CampaignPlanDetails | null => sharedCampaignPlan.campaignPlanDetails,
);

export const getCampaignPlanError = createSelector(
  [getSharedCampaignPlan],
  (sharedCampaignPlan): CampaignPlanError | null => sharedCampaignPlan.campaignPlanError,
);

export const getAllocationReportDealLines = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): SelectedLine[] => {
    if (!campaignPlanDetails) return [];

    return campaignPlanDetails.dealLines.map((line) => ({
      lineDefaultReportType: ReportType.SAVED,
      lineId: line.dealLineId,
      lineName: line.dealLineName,
      lineStatus: line.dealLineState,
    }));
  },
);

export const getCampaignPlanFilterOptions = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): CampaignPlanFilterOptions | null => campaignPlanDetails?.filterOptions ?? null,
);

export const getTouchedPoints = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): TouchedPoint[] => campaignPlanDetails?.touchedPoints ?? [],
);

export const getIsMapIncluded = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): boolean => campaignPlanDetails?.metaData.includeMap ?? true,
);

export const getIsPOIsIncluded = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): boolean => campaignPlanDetails?.metaData.includePOIs ?? true,
);

export const getAllocationReportConfig = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): CampaignPlanAllocationReportConfig | undefined =>
    campaignPlanDetails?.metaData.allocationReport ?? undefined,
);

export const getLocaleCode = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): string =>
    publicDefaultLocaleCodes[campaignPlanDetails?.metaData.isoCountryCode ?? ''] ?? publicDefaultLocaleCodes.GB,
);

export const getAllocationReportSelectAllDefault = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): boolean => campaignPlanDetails?.metaData.allocationReport?.dealLineSelect?.isDefault ?? false,
);

export const getAllocationReportSelectAllLocked = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): boolean => campaignPlanDetails?.metaData.allocationReport?.dealLineSelect?.isLocked ?? false,
);

export const getAllocationReportSelectHidden = createSelector(
  [getCampaignPlanDetails],
  (campaignPlanDetails): boolean => campaignPlanDetails?.metaData.allocationReport?.dealLineSelect?.isHidden ?? false,
);
